/**
 * OwnershipGateSettings — the admin on/off switch for the ownership publish
 * gate, shown above the Ownership Review queue.
 *
 * - A real switch (role="switch", keyboard operable) plus the state in words.
 * - Turning it OFF asks for a REQUIRED note in a confirm dialog; turning it ON
 *   is a single confirm click with no note.
 * - `env_forced_off`: the deployment setting wins — the switch shows off and
 *   disabled with a warning, because only an engineer can change it.
 * - The switch flips optimistically on confirm; a failed save reverts it and
 *   shows the message. Changes are announced via aria-live.
 *
 * Contract: GET/PUT /api/admin/ownership-verifications/settings (see
 * ownershipVerificationAgent.adminGetSettings / adminUpdateSettings).
 *
 * @param {object} props
 * @param {(settings: object) => void} [props.onSettingsChange] - called with
 *   every settings payload (load + save) so the parent can show its banner.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import { ownershipVerificationAgent } from '../../agents/ownershipVerificationAgent';
import OwnershipNotificationSettings from './OwnershipNotificationSettings';
import styles from './OwnershipGateSettings.module.css';

export const GATE_TITLE = 'Require ownership confirmation before publishing';
export const GATE_ON_TEXT = "On: new listings can't publish until ownership documents are reviewed";
export const GATE_OFF_TEXT = 'Off: listings can publish without ownership review';
export const GATE_OFF_CONFIRM_TEXT =
  'Sellers will be able to publish without an ownership review. Uploads, admin review and the buyer badge keep working. Changing the address of a live listing stays blocked.';
export const GATE_ENV_FORCED_TEXT =
  'Turned off by server configuration (OWNERSHIP_VERIFICATION_ENABLED). An engineer must change the deployment setting to turn it back on.';

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const onOff = (value) => (value === true ? 'On' : value === false ? 'Off' : 'Not set');

/** Admin-facing message for a failed save. */
function saveErrorMessage(err) {
  if (err?.detail?.code === 'note_required') {
    return 'A note is required to turn this off. Nothing was changed.';
  }
  if (err?.status === 403) return 'You need admin access to change this setting.';
  return err?.message || 'The setting could not be saved. Nothing was changed.';
}

export default function OwnershipGateSettings({ onSettingsChange }) {
  const uid = useId();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // null | 'off' | 'on' — which confirmation dialog is open.
  const [confirming, setConfirming] = useState(null);
  const [note, setNote] = useState('');
  // The value shown while a save is in flight (reverted on failure).
  const [optimistic, setOptimistic] = useState(null);
  const [announcement, setAnnouncement] = useState('');
  const noteRef = useRef(null);
  const switchRef = useRef(null);

  const onChangeRef = useRef(onSettingsChange);
  onChangeRef.current = onSettingsChange;
  const applySettings = useCallback((data) => {
    setSettings(data);
    if (onChangeRef.current) onChangeRef.current(data);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await ownershipVerificationAgent.adminGetSettings();
      if (!data || typeof data.enabled !== 'boolean') {
        throw new Error('The setting could not be loaded.');
      }
      applySettings(data);
    } catch (err) {
      console.error('Ownership gate settings load failed:', err?.status);
      setLoadError(
        err?.status === 403
          ? 'You need admin access to see this setting.'
          : err?.message || 'The setting could not be loaded.',
      );
    } finally {
      setLoading(false);
    }
  }, [applySettings]);

  useEffect(() => {
    load();
  }, [load]);

  const closeConfirm = useCallback(() => {
    setConfirming(null);
    setNote('');
    if (switchRef.current) switchRef.current.focus();
  }, []);

  const save = async (enabled, noteText) => {
    setConfirming(null);
    setNote('');
    setSaveError(null);
    setSaving(true);
    setOptimistic(enabled);
    setAnnouncement('Saving…');
    try {
      const data = await ownershipVerificationAgent.adminUpdateSettings({ enabled, note: noteText });
      applySettings(data);
      setAnnouncement(
        data?.enabled
          ? 'Ownership confirmation is now required before publishing.'
          : 'Ownership confirmation is no longer required before publishing.',
      );
    } catch (err) {
      console.error('Ownership gate settings save failed:', err?.status);
      const message = saveErrorMessage(err);
      setSaveError(message);
      setAnnouncement(`Not saved. ${message}`);
    } finally {
      // Success: `settings` now holds the truth. Failure: fall back to it.
      setOptimistic(null);
      setSaving(false);
      if (switchRef.current) switchRef.current.focus();
    }
  };

  const titleId = `${uid}-title`;
  const stateId = `${uid}-state`;

  if (loading && !settings) {
    return (
      <section className={styles.card} aria-labelledby={titleId} data-testid="ownership-gate-settings">
        <h3 id={titleId} className={styles.title}>{GATE_TITLE}</h3>
        <p className={styles.muted} role="status" data-testid="ownership-gate-loading">Loading setting…</p>
      </section>
    );
  }

  if (!settings) {
    return (
      <section className={styles.card} aria-labelledby={titleId} data-testid="ownership-gate-settings">
        <h3 id={titleId} className={styles.title}>{GATE_TITLE}</h3>
        <div className={styles.error} role="alert" data-testid="ownership-gate-load-error">
          <span>{loadError || 'The setting could not be loaded.'}</span>
          <button type="button" className={styles.secondaryBtn} onClick={load}>Try again</button>
        </div>
      </section>
    );
  }

  const envForcedOff = settings.env_forced_off === true;
  const effective = envForcedOff ? false : optimistic ?? settings.enabled === true;
  const history = Array.isArray(settings.history) ? settings.history : [];
  const changedBy = settings.updated_by?.name || settings.updated_by?.email || null;
  const changedAt = formatDateTime(settings.updated_at);
  const noteMissing = !note.trim();

  const handleToggle = () => {
    if (envForcedOff || saving) return;
    setSaveError(null);
    setConfirming(effective ? 'off' : 'on');
  };

  return (
    <>
    <section
      className={`${styles.card} ${effective ? '' : styles.cardOff}`}
      aria-labelledby={titleId}
      data-testid="ownership-gate-settings"
    >
      <div className={styles.visuallyHidden} aria-live="polite" aria-atomic="true" data-testid="ownership-gate-announcer">
        {announcement}
      </div>

      <div className={styles.row}>
        <div className={styles.text}>
          <h3 id={titleId} className={styles.title}>{GATE_TITLE}</h3>
          <p id={stateId} className={styles.state} data-testid="ownership-gate-state">
            {effective ? GATE_ON_TEXT : GATE_OFF_TEXT}
            {saving ? ' (saving…)' : ''}
          </p>
        </div>
        <button
          ref={switchRef}
          type="button"
          role="switch"
          aria-checked={effective}
          aria-labelledby={titleId}
          aria-describedby={stateId}
          aria-busy={saving ? 'true' : undefined}
          // Not `disabled` while saving: focus has to be able to return here.
          aria-disabled={saving ? 'true' : undefined}
          className={`${styles.switch} ${effective ? styles.switchOn : ''}`}
          onClick={handleToggle}
          disabled={envForcedOff}
          data-testid="ownership-gate-switch"
        >
          <span className={styles.thumb} aria-hidden="true" />
          <span className={styles.switchLabel} aria-hidden="true">{effective ? 'On' : 'Off'}</span>
        </button>
      </div>

      {envForcedOff && (
        <p className={styles.warning} role="status" data-testid="ownership-gate-env-forced">
          {GATE_ENV_FORCED_TEXT}
        </p>
      )}

      {saveError && (
        <p className={styles.error} role="alert" data-testid="ownership-gate-save-error">{saveError}</p>
      )}

      {(changedBy || changedAt) && (
        <p className={styles.muted} data-testid="ownership-gate-last-changed">
          Last changed{changedBy ? ` by ${changedBy}` : ''}{changedAt ? ` on ${changedAt}` : ''}
        </p>
      )}

      {history.length > 0 && (
        <details className={styles.history} data-testid="ownership-gate-history">
          <summary>Change history ({history.length})</summary>
          <ol className={styles.historyList}>
            {history.map((h, i) => (
              <li key={`${h.changed_at}-${i}`} className={styles.historyItem}>
                <span className={styles.historyWhen}>{formatDateTime(h.changed_at) || 'Unknown date'}</span>
                <span className={styles.historyWho}>{h.changed_by_name || 'Unknown'}</span>
                <span className={styles.historyChange}>
                  {onOff(h.old_enabled)} → {onOff(h.new_enabled)}
                </span>
                {h.note && <span className={styles.historyNote}>{h.note}</span>}
              </li>
            ))}
          </ol>
        </details>
      )}

      {/* Turn OFF: required note */}
      <Modal
        open={confirming === 'off'}
        onClose={closeConfirm}
        title="Turn off the ownership requirement?"
        size="sm"
        initialFocusRef={noteRef}
      >
        <form
          data-testid="ownership-gate-off-confirm"
          onSubmit={(e) => {
            e.preventDefault();
            if (!noteMissing) save(false, note);
          }}
        >
          <p className={styles.confirmText}>{GATE_OFF_CONFIRM_TEXT}</p>
          <label className={styles.label} htmlFor={`${uid}-note`}>Why are you turning this off? (required)</label>
          <textarea
            id={`${uid}-note`}
            ref={noteRef}
            className={styles.textarea}
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            aria-required="true"
            aria-describedby={noteMissing ? `${uid}-note-hint` : undefined}
            data-testid="ownership-gate-note"
          />
          {noteMissing && (
            <p className={styles.hint} id={`${uid}-note-hint`}>A note is required to turn this off.</p>
          )}
          <div className={styles.confirmActions}>
            <button type="button" className={styles.secondaryBtn} onClick={closeConfirm}>Cancel</button>
            <button
              type="submit"
              className={`${styles.primaryBtn} ${styles.dangerBtn}`}
              disabled={noteMissing}
              data-testid="ownership-gate-confirm-off"
            >
              Turn off
            </button>
          </div>
        </form>
      </Modal>

      {/* Turn ON: one confirm click, no note */}
      <Modal
        open={confirming === 'on'}
        onClose={closeConfirm}
        title="Turn on the ownership requirement?"
        size="sm"
      >
        <div data-testid="ownership-gate-on-confirm">
          <p className={styles.confirmText}>
            New listings won&apos;t be able to publish until their ownership documents are reviewed.
          </p>
          <div className={styles.confirmActions}>
            <button type="button" className={styles.secondaryBtn} onClick={closeConfirm}>Cancel</button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => save(true, null)}
              data-testid="ownership-gate-confirm-on"
            >
              Turn on
            </button>
          </div>
        </div>
      </Modal>
    </section>

    {/* Who gets emailed when a case needs review (same settings payload). */}
    <OwnershipNotificationSettings
      notifications={settings.notifications}
      onSettingsSaved={applySettings}
    />
    </>
  );
}
